import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { flowsApi, type Flow } from "./api";

const KEY = ["flows"] as const;

export function useFlows() {
  return useQuery({
    queryKey: [...KEY, "list"],
    queryFn: flowsApi.list,
    staleTime: 30_000,
  });
}

export function useFlow(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, "detail", id],
    queryFn: () => flowsApi.get(id!),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useCreateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      flowsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "list"] }),
  });
}

export function useUpdateFlow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Flow>) => flowsApi.update(id, data),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, "detail", id], updated);
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function useDeleteFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => flowsApi.remove(id),
    onSuccess: (_d, id) => {
      qc.setQueryData<Flow[]>([...KEY, "list"], (old) =>
        old?.filter((f) => f.id !== id),
      );
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function useValidateFlow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => flowsApi.validate(id),
    onSuccess: (data) => {
      qc.setQueryData<Flow | undefined>([...KEY, "detail", id], (old) =>
        old ? { ...old, is_valid: data.is_valid, validation_errors: data.errors } : old,
      );
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function usePublishFlow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => flowsApi.publish(id),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, "detail", id], updated);
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function useAvailableFunctions() {
  return useQuery({
    queryKey: ["integrations", "available-functions"],
    queryFn: flowsApi.availableFunctions,
    staleTime: 5 * 60_000,
  });
}
